import {
    FormControlLabel,
    Grid,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { FunctionComponent } from "react";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "../../../utils/useStore";
import {
    AgentCoachingStore,
    AgentCoachingTabs,
} from "./Stores/AgentCoachingStore";
import AgentsCoachingTable from "./Views/AgentsCoachingTable";
import theme from "Theme/AppTheme";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { Moment } from "moment";
import {
    DateRangeLabel,
    DateReferenceOption,
} from "stores/ComponentStores/DatePickerComponentStore";
import AcxSwitch from "components/UI/AcxSwitch";
import AgentManagersCoachingTable from "./Views/AgentManagersCoachingTable";
import { AcxTabPanel } from "components/UI/AcxTabPanel";
import CoachingCards from "./CoachingCards";

export const agentsDateIntervalOptions: DateRangeLabel[] = [
    "This Month",
    "Last Month",
];

const AgentCoaching: FunctionComponent = observer(() => {
    const store = useStore(AgentCoachingStore);
    const orgStore = useStore(AuthStore).orgStore;

    const handleChange = (
        event: React.ChangeEvent<{}>,
        newValue: AgentCoachingTabs,
    ) => {
        store.setCurrentTab(newValue);
    };

    const agentTableTabs = [
        { label: "Agents", disabled: false, tooltip: "" },
        {
            label: "Agent Managers",
        },
    ];

    React.useEffect(() => {
        if (orgStore && orgStore.selectedOrganization) {
            store.orgSelectorStore.selectOrg(orgStore.selectedOrganization);
        }
    }, [orgStore, orgStore?.selectedOrganization, store]);

    const referenceOptions = [
        DateReferenceOption.EvaluationCompleteDate,
        DateReferenceOption.InteractionDate,
    ];

    return (
        <div
            style={{
                height: "calc(100% - 100px)",
                width: "100%",
                padding: theme.spacing(2),
            }}
        >
            <Grid
                container
                item
                justifyContent={"space-between"}
                mb={2}
                alignItems={"flex-end"}
                xs={12}
            >
                <Grid item xs={3}>
                    <Typography component={"h1"} fontSize={24} fontWeight={600}>
                        {agentTableTabs[store.currentTab]?.label ?? ""}
                    </Typography>
                </Grid>
                <Grid container item xs={9} justifyContent={"flex-end"}>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <AcxSwitch
                                    onChange={store.toggleShowMachineEvals}
                                    checked={store.showMachineEvals}
                                />
                            }
                            label="Include AI Scored Evaluations"
                            sx={{ maxHeight: "32px" }} // aligns this with the dateRangeInput
                        />
                    </Grid>
                    <Grid item>
                        <AcxDateRangeInput
                            datePickerStore={store.datePickerStore}
                            displayDateType
                            dateReferenceOptions={referenceOptions}
                            defaultStartDate={store.datePickerStore.beginDate}
                            defaultEndDate={store.datePickerStore.endDate}
                            onSelect={(start: Moment, end: Moment) => {
                                store.datePickerStore.setBeginDate(start);
                                store.datePickerStore.setEndDate(end);
                            }}
                            useCustomDateTypeIds
                            hideLabel
                            sideBar
                            doubleCalendar
                            onDateRangeLabelChange={(
                                dateRangeLabel: DateRangeLabel,
                            ) => {
                                store.datePickerStore.setDateRangePreset(
                                    dateRangeLabel,
                                );
                            }}
                            dateRangeLabel={
                                store.datePickerStore.dateRangePresetLabel
                            }
                            wrapperStyle={{ maxWidth: "420px" }}
                            dateIntervalOptions={agentsDateIntervalOptions}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                direction="row"
                justifyContent="center"
                xs={12}
                container
                item
                gap={2}
            >
                <CoachingCards coachingDetails={store.coachingDetails} />
            </Grid>

            <Grid
                borderBottom={"2px solid"}
                borderColor={theme.palette.gray[200]}
            >
                <Tabs
                    value={store.currentTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        marginTop: "10px",
                    }}
                >
                    {agentTableTabs.map((tab, index) => (
                        <Tab
                            key={`${tab.label}_${index}`}
                            id={`agent-coaching-tab-${tab.label}_${index}`}
                            wrapped
                            label={
                                <Tooltip title={tab.tooltip}>
                                    {<span>{tab.label}</span>}
                                </Tooltip>
                            }
                            sx={{
                                minWidth: "min-content",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                paddingRight: "20px",
                            }}
                            disabled={tab.disabled}
                            style={{ pointerEvents: "auto" }}
                        />
                    ))}
                </Tabs>
            </Grid>
            <AcxTabPanel
                value={store.currentTab}
                index={AgentCoachingTabs.Agents}
            >
                <AgentsCoachingTable />
            </AcxTabPanel>
            <AcxTabPanel
                value={store.currentTab}
                index={AgentCoachingTabs.AgentManagers}
            >
                <AgentManagersCoachingTable />
            </AcxTabPanel>
        </div>
    );
});

export default AgentCoaching;
